/**
 * History page — lists all past verification jobs from the DB.
 */

import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { statusApi, downloadApi } from '../services/api';
import { CATEGORY_COLORS, CATEGORY_LABELS, PhoneCategory, HistoryJob } from '../types';
import { RerunFailedAnalysisButton } from '../components/RerunFailedAnalysisButton';

const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  completed: { bg: 'var(--status-job-completed-bg)', color: 'var(--status-job-completed-fg)' },
  failed:    { bg: 'var(--status-job-failed-bg)', color: 'var(--status-job-failed-fg)' },
  cancelled: { bg: 'var(--status-job-cancelled-bg)', color: 'var(--status-job-cancelled-fg)' },
  running:   { bg: 'var(--status-job-processing-bg)', color: 'var(--status-job-processing-fg)' },
  pending:   { bg: 'var(--status-job-pending-bg)', color: 'var(--status-job-pending-fg)' },
};

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const History: React.FC = () => {
  const [history, setHistory] = useState<HistoryJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // A4 — admin scoped view of one user's runs (read-only filter, not impersonation).
  const [searchParams] = useSearchParams();
  const viewUserId = searchParams.get('user') || undefined;
  const viewUserName = searchParams.get('name') || undefined;

  useEffect(() => {
    setIsLoading(true);
    (async () => {
      try {
        const res = await statusApi.getHistory(viewUserId);
        setHistory(res.history ?? res);
      } catch {
        setError('Failed to load history');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [viewUserId]);

  const fmt = (d: string) => new Date(d).toLocaleString();

  const totalPages = Math.max(1, Math.ceil(history.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const sliceStart = (safePage - 1) * pageSize;
  const sliceEnd   = sliceStart + pageSize;
  const pageItems  = history.slice(sliceStart, sliceEnd);
  const visibleEnd = Math.min(sliceEnd, history.length);

  const goTo = (p: number) => setPage(Math.max(1, Math.min(p, totalPages)));

  const handlePageSize = (n: number) => {
    setPageSize(n);
    setPage(1);
  };

  if (isLoading) {
    return (
      <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center', padding: 48 }}>
        <div className="spinner" />
        <p style={{ color: 'var(--text-soft)', marginTop: 16 }}>Loading history…</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <h2 style={{ color: 'var(--text-strong)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>📋 Run History</h2>

      {viewUserId && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
          marginBottom: 14, padding: '8px 12px', border: '1px solid var(--amber)',
          background: 'var(--surface-subtle)', color: 'var(--text-soft)', fontSize: 13,
        }}>
          <span>Viewing runs for <strong style={{ color: 'var(--amber)' }}>{viewUserName || viewUserId}</strong> (admin view)</span>
          <Link to="/history" style={{ color: 'var(--text-faint)', textDecoration: 'none' }}>← Back to all</Link>
        </div>
      )}

      {error && (
        <div style={{ background: 'var(--status-danger-bg)', color: 'var(--status-job-failed-fg)', padding: 12, borderRadius: 8, marginBottom: 16 }}>
          ❌ {error}
        </div>
      )}

      {history.length === 0 ? (
        <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 40, textAlign: 'center', background: 'var(--surface)' }}>
          <p style={{ color: 'var(--text-soft)', marginBottom: 16 }}>No verification jobs yet.</p>
          <Link
            to="/upload"
            style={{
              background: 'var(--indigo)', color: '#fff', padding: '10px 24px',
              borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: 14,
            }}
          >
            Start Your First Verification
          </Link>
        </div>
      ) : (
        <>
          {/* ── Top bar: status message + page-size picker ── */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 12, flexWrap: 'wrap', gap: 8,
          }}>
            <span style={{ fontSize: 13, color: 'var(--text-soft)' }}>
              Showing <strong>{sliceStart + 1}–{visibleEnd}</strong> of <strong>{history.length}</strong> jobs
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>Rows per page:</span>
              {PAGE_SIZE_OPTIONS.map(n => (
                <button
                  key={n}
                  onClick={() => handlePageSize(n)}
                  style={{
                    padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                    border: `1.5px solid ${pageSize === n ? 'var(--indigo)' : 'var(--border)'}`,
                    background: pageSize === n ? 'var(--indigo)' : 'var(--surface)',
                    color: pageSize === n ? '#fff' : 'var(--text)',
                    cursor: 'pointer',
                  }}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* ── Job cards ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pageItems.map((job) => {
              const st = STATUS_STYLE[job.status] ?? STATUS_STYLE.pending;
              const counts = job.category_counts ?? {};
              const aiAnalyzed = (counts['VOICEMAIL'] || 0) + (counts['SWITCHBOARD'] || 0) + (counts['UNKNOWN'] || 0);
              const twilioOnly = job.processed_count - aiAnalyzed;
              return (
                <div
                  key={job.job_id}
                  style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 20, background: 'var(--surface)' }}
                >
                  {/* Header row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{job.filename}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--text-soft)' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                          textTransform: 'uppercase', background: st.bg, color: st.color,
                        }}>
                          {job.status}
                        </span>
                        <span>{job.processed_count} / {job.total_numbers} numbers</span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 4 }}>
                        Created: {fmt(job.created_at)}
                        {job.completed_at && ` · Completed: ${fmt(job.completed_at)}`}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                      {job.status === 'completed' && (
                        <button
                          onClick={() => downloadApi.triggerDownload(job.job_id, job.filename)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', lineHeight: 1,
                            background: 'var(--indigo)', color: '#fff', border: '1px solid var(--indigo)',
                            padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                          }}
                        >
                          ⬇️ Download
                        </button>
                      )}
                      <Link
                        to={
                          job.status === 'processing' || job.status === 'pending'
                            ? `/processing/${job.job_id}`
                            : `/history/${job.job_id}`
                        }
                        style={{
                          display: 'inline-flex', alignItems: 'center', lineHeight: 1,
                          background: 'var(--surface-subtle)', color: 'var(--text)', border: '1px solid var(--border)',
                          padding: '8px 16px', borderRadius: 6, textDecoration: 'none', fontSize: 13,
                        }}
                      >
                        {job.status === 'processing' || job.status === 'pending'
                          ? 'Live View'
                          : 'View Details'}
                      </Link>
                      <RerunFailedAnalysisButton jobId={job.job_id} jobStatus={job.status} />
                    </div>
                  </div>

                  {/* AI vs Twilio breakdown */}
                  {job.processed_count > 0 && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                      <span style={{
                        padding: '2px 9px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background: 'var(--status-analysis-processing-bg)', color: 'var(--status-analysis-processing-fg)',
                      }}>
                        🤖 AI Analysis: {aiAnalyzed}
                      </span>
                      <span style={{
                        padding: '2px 9px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background: 'var(--status-success-bg)', color: 'var(--status-success-fg)',
                      }}>
                        📞 Twilio Only: {twilioOnly > 0 ? twilioOnly : 0}
                      </span>
                    </div>
                  )}

                  {/* Category chips */}
                  {Object.keys(counts).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {(Object.entries(counts) as [string, number][])
                        .sort(([, a], [, b]) => b - a)
                        .map(([cat, count]) => (
                          <span
                            key={cat}
                            style={{
                              padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 500,
                              background: CATEGORY_COLORS[cat as PhoneCategory] || 'var(--status-neutral-bg)',
                            }}
                          >
                            {CATEGORY_LABELS[cat as PhoneCategory] || cat}: {count}
                          </span>
                        ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Pagination controls ── */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              gap: 8, marginTop: 20, flexWrap: 'wrap',
            }}>
              <button
                onClick={() => goTo(safePage - 1)}
                disabled={safePage === 1}
                style={{
                  padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                  border: '1.5px solid var(--border)',
                  background: safePage === 1 ? 'var(--surface-muted)' : 'var(--surface)',
                  color: safePage === 1 ? 'var(--text-faint)' : 'var(--text)',
                  cursor: safePage === 1 ? 'not-allowed' : 'pointer',
                }}
              >
                ← Previous
              </button>

              <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600, minWidth: 120, textAlign: 'center' }}>
                Page {safePage} of {totalPages}
              </span>

              <button
                onClick={() => goTo(safePage + 1)}
                disabled={safePage === totalPages}
                style={{
                  padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                  border: '1.5px solid var(--border)',
                  background: safePage === totalPages ? 'var(--surface-muted)' : 'var(--surface)',
                  color: safePage === totalPages ? 'var(--text-faint)' : 'var(--text)',
                  cursor: safePage === totalPages ? 'not-allowed' : 'pointer',
                }}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default History;
